import {
  CharacterAlignment,
  StoryTimings,
  WordTiming,
  alignmentDuration,
  segmentAlignment,
  validateCharacterAlignment,
} from "@baiki/interfaces";

/**
 * Converts character-level alignment from ElevenLabs to word-level timing.
 *
 * Word boundaries come from `segmentAlignment` in @baiki/interfaces, shared with
 * the backend converter and the Whisper re-aligner so the three cannot disagree
 * about where a word ends — they did once, and Italian stories rendered on iOS
 * as "Perch la citt". What is web-specific stays here: rejecting corrupt
 * alignments, quality telemetry, and the duplicate-timestamp interpolation.
 *
 * @param alignment Character-level alignment data from ElevenLabs
 * @returns Word-level timing data
 * @throws Error if alignment data is invalid or corrupted
 */
export function convertAlignmentToWords(
  alignment: CharacterAlignment,
): StoryTimings {
  // Validate input alignment data
  const validationErrors = validateCharacterAlignment(alignment);
  if (validationErrors) {
    const errorMsg = `Invalid character alignment data:\n${validationErrors.join("\n")}`;
    console.error("[convertAlignmentToWords]", errorMsg);
    throw new Error(errorMsg);
  }

  // Basic sanity check
  if (alignment.characters.length === 0) {
    console.warn("[convertAlignmentToWords] Empty character alignment data");
    return {
      words: [],
      duration: 0,
      generatedAt: new Date(),
    };
  }

  const words = segmentAlignment(alignment);
  const duration = alignmentDuration(alignment);

  // Track statistics for quality monitoring
  let duplicateTimestampCount = 0;
  let zeroDurationCount = 0;

  for (let i = 0; i < alignment.characters.length; i++) {
    const startTime = alignment.character_start_times_seconds[i];
    if (startTime === alignment.character_end_times_seconds[i]) {
      zeroDurationCount++;
    }
    if (i > 0 && startTime === alignment.character_start_times_seconds[i - 1]) {
      duplicateTimestampCount++;
    }
  }

  for (const word of words) {
    if (word.endTime < word.startTime) {
      console.error(
        `[convertAlignmentToWords] Invalid word timing for "${word.word}": ` +
          `endTime (${word.endTime}) < startTime (${word.startTime})`,
      );
    }
  }

  // Log quality warnings
  const charCount = alignment.characters.length;
  if (zeroDurationCount > charCount * 0.1) {
    console.warn(
      `[convertAlignmentToWords] High rate of zero-duration characters: ` +
        `${zeroDurationCount}/${charCount} (${((zeroDurationCount / charCount) * 100).toFixed(1)}%)`,
    );
  }
  if (duplicateTimestampCount > charCount * 0.1) {
    console.warn(
      `[convertAlignmentToWords] High rate of duplicate timestamps: ` +
        `${duplicateTimestampCount}/${charCount} (${((duplicateTimestampCount / charCount) * 100).toFixed(1)}%)`,
    );
  }

  // Post-process words to fix duplicate timestamps
  const processedWords = postProcessTimings(words);

  // Validate word-level timings
  const wordQualityIssues = validateWordTimings(processedWords);
  if (wordQualityIssues.length > 0) {
    console.warn(
      `[convertAlignmentToWords] Word timing quality issues:\n${wordQualityIssues.join("\n")}`,
    );
  }

  console.log(
    `[convertAlignmentToWords] Converted ${charCount} characters to ${processedWords.length} words ` +
      `(duration: ${duration.toFixed(2)}s, zero-duration chars: ${zeroDurationCount}, ` +
      `duplicate timestamps: ${duplicateTimestampCount})`,
  );

  return {
    words: processedWords,
    duration,
    generatedAt: new Date(),
  };
}

/**
 * Post-process word timings to fix duplicate timestamps
 * When ElevenLabs returns duplicate timestamps, interpolate between them
 * to ensure each word has a unique time range
 *
 * @param words Raw word timings (may contain duplicates)
 * @returns Processed word timings with interpolated timestamps
 */
export function postProcessTimings(words: WordTiming[]): WordTiming[] {
  if (words.length === 0) return words;

  const result: WordTiming[] = [];
  let interpolatedCount = 0;

  for (let i = 0; i < words.length; i++) {
    const word = words[i];

    // Check if this word has same timestamp as next word(s)
    if (i < words.length - 1 && word.startTime === words[i + 1].startTime) {
      // Find end of duplicate group
      let groupEnd = i + 1;
      while (
        groupEnd < words.length &&
        words[groupEnd].startTime === word.startTime
      ) {
        groupEnd++;
      }

      // Calculate time to distribute across group
      const groupCount = groupEnd - i;
      const nextTime =
        groupEnd < words.length
          ? words[groupEnd].startTime
          : word.endTime + (word.endTime - word.startTime) * groupCount;

      // Calculate time slice per word
      const timeSlice = (nextTime - word.startTime) / groupCount;

      // Interpolate timestamps for group
      for (let j = i; j < groupEnd; j++) {
        const offsetFromGroupStart = j - i;
        result.push({
          ...words[j],
          startTime: word.startTime + offsetFromGroupStart * timeSlice,
          endTime: word.startTime + (offsetFromGroupStart + 1) * timeSlice,
        });
        interpolatedCount++;
      }

      i = groupEnd - 1; // Skip processed group (loop will increment)
    } else {
      // No duplicate - keep original timing
      result.push(word);
    }
  }

  if (interpolatedCount > 0) {
    console.log(
      `[postProcessTimings] Interpolated ${interpolatedCount}/${words.length} words with duplicate timestamps`,
    );
  }

  return result;
}

/**
 * Validates word-level timing data for quality issues
 * Returns array of warning messages (empty if no issues)
 */
function validateWordTimings(words: WordTiming[]): string[] {
  const warnings: string[] = [];

  if (words.length === 0) {
    return warnings;
  }

  let zeroDurationWords = 0;
  let duplicateTimestampWords = 0;
  const timestampMap = new Map<number, number[]>(); // startTime -> word indices

  for (let i = 0; i < words.length; i++) {
    const word = words[i];

    // Check for zero-duration words
    if (word.endTime === word.startTime) {
      zeroDurationWords++;
    }

    // Track duplicate timestamps
    const existing = timestampMap.get(word.startTime);
    if (existing) {
      existing.push(i);
      duplicateTimestampWords++;
    } else {
      timestampMap.set(word.startTime, [i]);
    }

    // Check for backwards time
    if (i > 0 && word.startTime < words[i - 1].startTime) {
      warnings.push(
        `Word #${i} "${word.word}" has startTime (${word.startTime}) before previous word "${words[i - 1].word}" (${words[i - 1].startTime})`,
      );
    }
  }

  // Report duplicate timestamp groups
  if (duplicateTimestampWords > 0) {
    const largestGroup = Math.max(
      ...Array.from(timestampMap.values()).map((indices) => indices.length),
    );
    warnings.push(
      `${duplicateTimestampWords} words share timestamps with others ` +
        `(largest group: ${largestGroup} words at same timestamp)`,
    );
  }

  // Report zero-duration words
  if (zeroDurationWords > words.length * 0.05) {
    warnings.push(
      `${zeroDurationWords}/${words.length} words have zero duration ` +
        `(${((zeroDurationWords / words.length) * 100).toFixed(1)}%)`,
    );
  }

  return warnings;
}

/**
 * Finds the currently playing word based on playback time
 * Uses a "sticky" approach - keeps highlighting the most recent word that started
 * until the next word begins, rather than only highlighting during exact timing windows
 *
 * Handles corrupted timing data gracefully:
 * - When multiple words share the same timestamp, picks the first one
 * - Falls back to position-based estimation if timing data is severely corrupted
 *
 * @param timings Word-level timing data
 * @param currentTime Current playback time in seconds
 * @returns Index of current word, or -1 if none
 */
export function getCurrentWordIndex(
  timings: StoryTimings,
  currentTime: number,
): number {
  if (!timings.words.length) return -1;

  // Before first word starts
  if (currentTime < timings.words[0].startTime) {
    return -1;
  }

  // After last word ends (with small buffer)
  const lastWord = timings.words[timings.words.length - 1];
  if (currentTime > lastWord.endTime + 0.5) {
    return -1;
  }

  // Find the most recent word that has started
  // This ensures we always have a word highlighted, even during gaps
  // IMPORTANT: Iterate forward (not backward) to prefer FIRST word when timestamps are duplicated
  let bestMatch = -1;

  for (let i = 0; i < timings.words.length; i++) {
    const word = timings.words[i];

    if (currentTime >= word.startTime) {
      // Handle zero-duration words from corrupted ElevenLabs v3 data
      // If this is a zero-duration word, we need special handling
      if (word.endTime === word.startTime && currentTime >= word.startTime) {
        // Find the end of the zero-duration group (all words with same start/end time)
        let groupEnd = i;
        while (
          groupEnd < timings.words.length - 1 &&
          timings.words[groupEnd + 1].startTime === word.startTime &&
          timings.words[groupEnd + 1].endTime === word.startTime
        ) {
          groupEnd++;
        }

        // Find the next word that has moved past this timestamp
        // (could be zero-duration at same start but different end, or different start)
        let nextIndex = groupEnd + 1;
        while (
          nextIndex < timings.words.length &&
          timings.words[nextIndex].startTime === word.startTime &&
          timings.words[nextIndex].endTime === word.startTime
        ) {
          nextIndex++;
        }

        const nextWord =
          nextIndex < timings.words.length ? timings.words[nextIndex] : null;

        // If there's a next word and we haven't reached its end time yet, stay on first word in group
        if (nextWord && currentTime < nextWord.endTime) {
          return i; // Return first word in zero-duration group
        }

        // We've passed the entire group, skip to the next word
        i = nextIndex - 1; // -1 because loop will increment
        continue;
      }

      // Normal case: check if we've passed this word
      const nextWordStart =
        i < timings.words.length - 1
          ? timings.words[i + 1].startTime
          : Infinity;
      const wordEnd = Math.max(word.endTime, nextWordStart - 0.001);

      // If current time is within this word's range, use it
      if (currentTime <= wordEnd) {
        bestMatch = i;
        break; // Use first matching word (important for duplicate timestamps)
      } else {
        // This word has ended, but keep it as fallback
        bestMatch = i;
      }
    } else {
      // Haven't reached this word yet
      break;
    }
  }

  return bestMatch;
}
